import { createUserWithEmailAndPassword, fetchSignInMethodsForEmail, sendPasswordResetEmail, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { collection, addDoc, updateDoc, doc, getDoc, setDoc, serverTimestamp, query, where, getDocs, limit, Timestamp } from 'firebase/firestore';
import { auth, db } from '../../../firebase';
import { CLIENT_MEMBERSHIP_PLANS } from '../../../constants/membershipPlans';
import { getAuthContinueUrlBase } from '../../../constants';

/**
 * Servicio para asignar credenciales a clientes cuando se activa un perfil premium
 */
class CredencialesClienteService {
  
  /**
   * Asignar credenciales a un cliente y enviar notificación por email
   * @param {Object} datosCliente - Datos del cliente
   * @param {string} datosCliente.email - Email del cliente
   * @param {string} datosCliente.nombre - Nombre del cliente
   * @param {string} datosCliente.telefono - Teléfono del cliente
   * @param {string} password - Contraseña para el cliente (si no existe, se genera una)
   * @param {Object} datosPerfil - Datos del perfil premium activado
   * @param {string} datosPerfil.marca - Marca del vehículo
   * @param {string} datosPerfil.modelo - Modelo del vehículo
   * @param {number} datosPerfil.año - Año del vehículo
   * @param {string} datosPerfil.concesionariaNombre - Nombre de la concesionaria
   * @param {string} datosPerfil.concesionariaId - ID de la concesionaria (opcional)
   * @returns {Promise<Object>} Resultado con uid y credenciales
   */
  async asignarCredencialesYNotificar(datosCliente, password, datosPerfil) {
    try {
      console.log('🔐 Iniciando asignación de credenciales para cliente:', datosCliente.email);
      
      // 1. Verificar si el usuario ya existe
      let userUid = null;
      let passwordGenerada = password;
      let esUsuarioNuevo = false;
      
      try {
        const signInMethods = await fetchSignInMethodsForEmail(auth, datosCliente.email);
        
        if (signInMethods.length > 0) {
          // Usuario ya existe en Firebase Auth
          console.log('⚠️ Usuario ya existe en Firebase Auth');
          
          // Buscar el UID en la colección usuarios
          const usuariosQuery = query(
            collection(db, 'usuarios'),
            where('email', '==', datosCliente.email),
            limit(1)
          );
          const usuariosSnapshot = await getDocs(usuariosQuery);
          
          if (!usuariosSnapshot.empty) {
            userUid = usuariosSnapshot.docs[0].id;
            console.log('✅ Usuario encontrado en Firestore:', userUid);
          } else {
            // Usuario existe en Auth pero no en Firestore
            console.log('⚠️ Usuario existe en Auth pero no en Firestore');
            
            // Intentar obtener el UID haciendo signIn si tenemos contraseña
            if (passwordGenerada) {
              try {
                console.log('⚠️ Intentando signIn para obtener UID del usuario existente...');
                const signInResult = await signInWithEmailAndPassword(
                  auth,
                  datosCliente.email,
                  passwordGenerada
                );
                userUid = signInResult.user.uid;
                console.log('✅ UID obtenido mediante signIn:', userUid);
                // Cerrar sesión inmediatamente
                await signOut(auth);
              } catch (signInError) {
                console.error('❌ Error en signIn para obtener UID:', signInError);
                // Si falla el signIn (contraseña incorrecta), lanzar error descriptivo
                if (signInError.code === 'auth/wrong-password' || signInError.code === 'auth/invalid-credential') {
                  throw new Error(
                    'El usuario ya existe en el sistema pero la contraseña proporcionada no es correcta. ' +
                    'Por favor, usa la opción "Enviar restablecimiento de contraseña" para que el cliente pueda establecer su propia contraseña, ' +
                    'o contacta al administrador para vincular el usuario manualmente.'
                  );
                } else {
                  throw new Error(
                    'El usuario ya existe en el sistema pero no se pudo vincular. ' +
                    'Error: ' + (signInError.message || signInError.code) + '. ' +
                    'Por favor, contacta al administrador para vincular el usuario manualmente.'
                  );
                }
              }
            } else {
              // No hay contraseña, lanzar error descriptivo con sugerencia
              throw new Error(
                'El usuario ya existe en el sistema pero no se encontró en la base de datos. ' +
                'Por favor, usa la opción "Enviar restablecimiento de contraseña" para que el cliente pueda establecer su propia contraseña, ' +
                'o contacta al administrador para vincular el usuario manualmente.'
              );
            }
          }
        } else {
          // Usuario no existe, crear uno nuevo
          esUsuarioNuevo = true;
          
          // Generar contraseña si no se proporciona
          if (!passwordGenerada) {
            passwordGenerada = this.generarPasswordTemporal();
          }
          
          console.log('🔐 Creando nuevo usuario en Firebase Auth...');
          const userCredential = await createUserWithEmailAndPassword(
            auth,
            datosCliente.email,
            passwordGenerada
          );
          
          userUid = userCredential.user.uid;
          console.log('✅ Nuevo usuario creado:', userUid);
        }
      } catch (authError) {
        if (authError.code === 'auth/email-already-in-use') {
          // El usuario se creó entre la verificación y el intento de creación
          console.log('⚠️ Usuario creado entre verificación e intento, buscando...');
          
          // Primero intentar buscar en Firestore
          const usuariosQuery = query(
            collection(db, 'usuarios'),
            where('email', '==', datosCliente.email),
            limit(1)
          );
          const usuariosSnapshot = await getDocs(usuariosQuery);
          
          if (!usuariosSnapshot.empty) {
            userUid = usuariosSnapshot.docs[0].id;
            console.log('✅ Usuario encontrado en Firestore después del error:', userUid);
          } else {
            // Usuario existe en Auth pero no en Firestore
            // Intentar obtener el UID haciendo signIn si tenemos contraseña
            if (passwordGenerada) {
              try {
                console.log('⚠️ Intentando signIn para obtener UID del usuario existente...');
                const signInResult = await signInWithEmailAndPassword(
                  auth,
                  datosCliente.email,
                  passwordGenerada
                );
                userUid = signInResult.user.uid;
                console.log('✅ UID obtenido mediante signIn:', userUid);
                // Cerrar sesión inmediatamente
                await signOut(auth);
              } catch (signInError) {
                console.error('❌ Error en signIn para obtener UID:', signInError);
                // Si falla el signIn (contraseña incorrecta), lanzar error descriptivo
                if (signInError.code === 'auth/wrong-password' || signInError.code === 'auth/invalid-credential') {
                  throw new Error(
                    'El usuario ya existe en el sistema pero la contraseña proporcionada no es correcta. ' +
                    'Por favor, usa la opción "Enviar restablecimiento de contraseña" para que el cliente pueda establecer su propia contraseña, ' +
                    'o contacta al administrador para vincular el usuario manualmente.'
                  );
                } else {
                  throw new Error(
                    'El usuario ya existe en el sistema pero no se pudo vincular. ' +
                    'Error: ' + (signInError.message || signInError.code) + '. ' +
                    'Por favor, contacta al administrador para vincular el usuario manualmente.'
                  );
                }
              }
            } else {
              // No hay contraseña, lanzar error descriptivo con sugerencia
              throw new Error(
                'El usuario ya existe en el sistema pero no se encontró en la base de datos. ' +
                'Por favor, usa la opción "Enviar restablecimiento de contraseña" para que el cliente pueda establecer su propia contraseña, ' +
                'o contacta al administrador para vincular el usuario manualmente.'
              );
            }
          }
        } else {
          throw authError;
        }
      }
      
      // 2. Crear o actualizar registro en Firestore 'usuarios'
      const usuarioData = {
        uid: userUid,
        email: datosCliente.email,
        nombre: datosCliente.nombre,
        telefono: datosCliente.telefono || '',
        rol: 'cliente', // Rol siempre es 'cliente'
        activo: true,
        fechaCreacion: serverTimestamp(),
        // Información de que fue creado por concesionaria
        creadoPorConcesionaria: true,
        concesionariaId: datosPerfil.concesionariaId || null,
        concesionariaNombre: datosPerfil.concesionariaNombre || null,
        fechaCreacionPorConcesionaria: serverTimestamp(),
        tienePerfilPremium: true,
        perfilesPremium: [{
          perfilId: datosPerfil.perfilId,
          marca: datosPerfil.marca,
          modelo: datosPerfil.modelo,
          año: datosPerfil.año,
          fechaActivacion: Timestamp.fromDate(new Date()), // Usar Timestamp en lugar de serverTimestamp() dentro de arrays
          concesionariaNombre: datosPerfil.concesionariaNombre,
          concesionariaId: datosPerfil.concesionariaId || null
        }]
      };
      
      const usuarioRef = doc(db, 'usuarios', userUid);
      const usuarioSnap = await getDoc(usuarioRef);
      
      if (usuarioSnap.exists()) {
        // Actualizar usuario existente
        const datosExistentes = usuarioSnap.data();
        const perfilesPremium = datosExistentes.perfilesPremium || [];
        
        // Verificar si el perfil ya está en la lista
        const perfilExiste = perfilesPremium.some(p => p.perfilId === datosPerfil.perfilId);
        if (!perfilExiste) {
          perfilesPremium.push({
            perfilId: datosPerfil.perfilId,
            marca: datosPerfil.marca,
            modelo: datosPerfil.modelo,
            año: datosPerfil.año,
            fechaActivacion: Timestamp.fromDate(new Date()), // Usar Timestamp en lugar de serverTimestamp() dentro de arrays
            concesionariaNombre: datosPerfil.concesionariaNombre,
            concesionariaId: datosPerfil.concesionariaId || null
          });
        } else {
          // Actualizar información de concesionaria si el perfil ya existe
          const perfilIndex = perfilesPremium.findIndex(p => p.perfilId === datosPerfil.perfilId);
          if (perfilIndex !== -1) {
            perfilesPremium[perfilIndex] = {
              ...perfilesPremium[perfilIndex],
              concesionariaNombre: datosPerfil.concesionariaNombre || perfilesPremium[perfilIndex].concesionariaNombre,
              concesionariaId: datosPerfil.concesionariaId || perfilesPremium[perfilIndex].concesionariaId || null
            };
          }
        }
        
        // SIEMPRE asegurar que el rol sea 'cliente' y registrar información de concesionaria
        const datosActualizacion = {
          nombre: datosCliente.nombre,
          telefono: datosCliente.telefono || datosExistentes.telefono || '',
          tienePerfilPremium: true,
          perfilesPremium: perfilesPremium,
          fechaActualizacion: serverTimestamp(),
          rol: 'cliente', // SIEMPRE asignar rol 'cliente' para usuarios de concesionaria
          // Registrar información de concesionaria (si no está ya registrada)
          creadoPorConcesionaria: true,
          concesionariaId: datosPerfil.concesionariaId || datosExistentes.concesionariaId || null,
          concesionariaNombre: datosPerfil.concesionariaNombre || datosExistentes.concesionariaNombre || null
        };
        
        // Si es la primera vez que se marca como creado por concesionaria, guardar fecha
        if (!datosExistentes.creadoPorConcesionaria) {
          datosActualizacion.fechaCreacionPorConcesionaria = serverTimestamp();
        }
        
        // Log para depuración si el rol estaba diferente
        if (!datosExistentes.rol || datosExistentes.rol !== 'cliente') {
          console.log('⚠️ Usuario existente sin rol o con rol diferente. Actualizando rol a "cliente"');
          console.log('   Rol anterior:', datosExistentes.rol || 'sin rol');
          console.log('   Rol nuevo: cliente');
        }
        
        if (!datosExistentes.creadoPorConcesionaria) {
          console.log('📝 Marcando usuario como creado por concesionaria:', datosPerfil.concesionariaNombre);
        }
        
        await updateDoc(usuarioRef, datosActualizacion);
        console.log('✅ Usuario actualizado con rol "cliente" y marcado como creado por concesionaria, UID:', userUid);
      } else {
        // Crear nuevo usuario - asegurar que siempre tenga rol 'cliente'
        const nuevoUsuarioData = {
          ...usuarioData,
          rol: 'cliente' // Asegurar explícitamente el rol
        };
        console.log('📝 Creando nuevo usuario con datos:', {
          uid: nuevoUsuarioData.uid,
          email: nuevoUsuarioData.email,
          rol: nuevoUsuarioData.rol,
          nombre: nuevoUsuarioData.nombre
        });
        await setDoc(usuarioRef, nuevoUsuarioData);
        console.log('✅ Nuevo usuario creado con rol "cliente" en Firestore');
      }
      
      console.log('✅ Registro de usuario creado/actualizado en Firestore');
      
      // 3. Crear o actualizar perfil de cliente en 'perfiles_clientes' con plan premium
      await this.crearOActualizarPerfilClientePremium(userUid, datosCliente, datosPerfil);
      
      // 4. Crear o actualizar membresía premium en 'membresias_clientes'
      await this.crearOActualizarMembresiaPremium(userUid, datosPerfil);
      
      // 5. Enviar email de notificación
      await this.enviarEmailNotificacion(datosCliente, passwordGenerada, datosPerfil, esUsuarioNuevo);
      
      return {
        success: true,
        uid: userUid,
        email: datosCliente.email,
        password: passwordGenerada,
        esUsuarioNuevo: esUsuarioNuevo
      };
      
    } catch (error) {
      console.error('❌ Error asignando credenciales:', error);
      throw error;
    }
  }
  
  /**
   * Generar contraseña temporal segura
   * @returns {string} Contraseña generada
   */
  generarPasswordTemporal() {
    const caracteres = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%&*';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += caracteres.charAt(Math.floor(Math.random() * caracteres.length));
    }
    return password;
  }

  /**
   * Crear o actualizar perfil de cliente en 'perfiles_clientes' con plan premium
   * @param {string} userUid - UID del usuario
   * @param {Object} datosCliente - Datos del cliente
   * @param {Object} datosPerfil - Datos del perfil premium
   */
  async crearOActualizarPerfilClientePremium(userUid, datosCliente, datosPerfil) {
    try {
      // Buscar perfil existente por UID o email
      let perfilRef = null;
      let perfilSnap = null;
      
      // Intentar buscar por UID
      try {
        const perfilesQuery = query(
          collection(db, 'perfiles_clientes'),
          where('uid', '==', userUid),
          limit(1)
        );
        const perfilesSnapshot = await getDocs(perfilesQuery);
        if (!perfilesSnapshot.empty) {
          perfilRef = doc(db, 'perfiles_clientes', perfilesSnapshot.docs[0].id);
          perfilSnap = await getDoc(perfilRef);
        }
      } catch (error) {
        console.log('No se encontró perfil por UID, buscando por email...');
      }

      // Si no se encontró por UID, buscar por email
      if (!perfilSnap || !perfilSnap.exists()) {
        try {
          const perfilesQuery = query(
            collection(db, 'perfiles_clientes'),
            where('email', '==', datosCliente.email.toLowerCase().trim()),
            limit(1)
          );
          const perfilesSnapshot = await getDocs(perfilesQuery);
          if (!perfilesSnapshot.empty) {
            perfilRef = doc(db, 'perfiles_clientes', perfilesSnapshot.docs[0].id);
            perfilSnap = await getDoc(perfilRef);
          }
        } catch (error) {
          console.log('No se encontró perfil por email');
        }
      }

      const perfilClienteData = {
        uid: userUid,
        email: datosCliente.email.toLowerCase().trim(),
        nombres: datosCliente.nombre.split(' ')[0] || datosCliente.nombre,
        apellidos: datosCliente.nombre.split(' ').slice(1).join(' ') || '',
        telefono: datosCliente.telefono || '',
        plan: 'premium', // ✅ Marcar como premium
        membresia: 'premium', // ✅ Marcar como premium
        tienePerfilPremium: true,
        creadoPorConcesionaria: true,
        concesionariaId: datosPerfil.concesionariaId || null,
        concesionariaNombre: datosPerfil.concesionariaNombre || null,
        estado: 'activo',
        fechaRegistro: serverTimestamp(),
        fechaCreacion: serverTimestamp(),
        fechaActivacionPremium: serverTimestamp()
      };

      if (perfilSnap && perfilSnap.exists()) {
        // Actualizar perfil existente
        await updateDoc(perfilRef, {
          ...perfilClienteData,
          fechaActualizacion: serverTimestamp()
        });
        console.log('✅ Perfil de cliente actualizado con plan premium');
      } else {
        // Crear nuevo perfil
        perfilRef = doc(db, 'perfiles_clientes', userUid);
        await setDoc(perfilRef, perfilClienteData);
        console.log('✅ Perfil de cliente creado con plan premium');
      }
    } catch (error) {
      console.error('⚠️ Error creando/actualizando perfil de cliente premium:', error);
      // No lanzar error para no bloquear el flujo principal
    }
  }

  /**
   * Crear o actualizar membresía premium en 'membresias_clientes'
   * @param {string} userUid - UID del usuario
   * @param {Object} datosPerfil - Datos del perfil premium
   */
  async crearOActualizarMembresiaPremium(userUid, datosPerfil) {
    try {
      const membresiaRef = doc(db, 'membresias_clientes', userUid);
      const membresiaSnap = await getDoc(membresiaRef);

      // Calcular fecha de expiración (12 meses desde ahora)
      const fechaActivacion = new Date();
      const fechaExpiracion = new Date();
      fechaExpiracion.setMonth(fechaExpiracion.getMonth() + 12);

      const membresiaData = {
        planId: 'premium',
        nivel: 'premium',
        beneficios: CLIENT_MEMBERSHIP_PLANS.PREMIUM.features,
        status: 'activa',
        fechaInicio: Timestamp.fromDate(fechaActivacion),
        fechaExpiracion: Timestamp.fromDate(fechaExpiracion),
        fechaUltimaActividad: Timestamp.fromDate(fechaActivacion),
        creadoPorConcesionaria: true,
        concesionariaId: datosPerfil.concesionariaId || null,
        concesionariaNombre: datosPerfil.concesionariaNombre || null,
        puntos: 0
      };

      if (membresiaSnap.exists()) {
        // Actualizar membresía existente
        await updateDoc(membresiaRef, {
          ...membresiaData,
          fechaActualizacion: serverTimestamp()
        });
        console.log('✅ Membresía premium actualizada');
      } else {
        // Crear nueva membresía
        await setDoc(membresiaRef, {
          ...membresiaData,
          fechaCreacion: serverTimestamp()
        });
        console.log('✅ Membresía premium creada');
      }
    } catch (error) {
      console.error('⚠️ Error creando/actualizando membresía premium:', error);
      // No lanzar error para no bloquear el flujo principal
    }
  }
  
  /**
   * Enviar email de notificación al cliente
   * @param {Object} datosCliente - Datos del cliente
   * @param {string} password - Contraseña asignada
   * @param {Object} datosPerfil - Datos del perfil premium
   * @param {boolean} esUsuarioNuevo - Si es un usuario nuevo
   */
  async enviarEmailNotificacion(datosCliente, password, datosPerfil, esUsuarioNuevo) {
    try {
      // Usar Cloud Function para enviar el email
      const functionsUrl = import.meta.env.VITE_FUNCTIONS_URL || 
        `https://${import.meta.env.VITE_FIREBASE_PROJECT_ID || 'huamachuco-c0d9f'}-us-central1.cloudfunctions.net`;
      
      const emailData = {
        to: datosCliente.email,
        subject: esUsuarioNuevo 
          ? '🎉 ¡Bienvenido a AV 10 de Julio! Tu perfil premium está listo'
          : '✅ Tu nuevo perfil premium está activo',
        template: 'perfil_premium_activado',
        datos: {
          nombreCliente: datosCliente.nombre,
          email: datosCliente.email,
          password: password,
          marca: datosPerfil.marca,
          modelo: datosPerfil.modelo,
          año: datosPerfil.año,
          concesionariaNombre: datosPerfil.concesionariaNombre,
          esUsuarioNuevo: esUsuarioNuevo,
          fechaActivacion: new Date().toLocaleDateString('es-CL'),
          loginUrl: `${import.meta.env.VITE_BASE_URL || window.location.origin}/login`
        }
      };
      
      const response = await fetch(`${functionsUrl}/enviarEmailNotificacion`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(emailData),
      });
      
      if (!response.ok) {
        console.warn('⚠️ No se pudo enviar email, pero se continuará con el proceso');
        // Registrar intento de envío en Firestore
        await this.registrarIntentoEmail(datosCliente.email, emailData, false);
      } else {
        console.log('✅ Email de notificación enviado exitosamente');
        await this.registrarIntentoEmail(datosCliente.email, emailData, true);
      }
      
    } catch (error) {
      console.error('❌ Error enviando email de notificación:', error);
      // No lanzar error, solo registrar
      await this.registrarIntentoEmail(datosCliente.email, { error: error.message }, false);
    }
  }
  
  /**
   * Registrar intento de envío de email en Firestore
   */
  async registrarIntentoEmail(email, datos, exitoso) {
    try {
      await addDoc(collection(db, 'email_logs'), {
        email,
        tipo: 'perfil_premium_activado',
        datos,
        exitoso,
        fechaEnvio: serverTimestamp(),
        proveedor: 'cloud_function'
      });
    } catch (error) {
      console.error('Error registrando intento de email:', error);
    }
  }

  /**
   * Obtener información de credenciales de un perfil
   * @param {string} perfilId - ID del perfil de vehículo
   * @returns {Promise<Object>} Información de credenciales
   */
  async obtenerInfoCredenciales(perfilId) {
    try {
      const perfilRef = doc(db, 'perfiles_vehiculos_concesionaria', perfilId);
      const perfilSnap = await getDoc(perfilRef);
      
      if (!perfilSnap.exists()) {
        throw new Error('Perfil no encontrado');
      }
      
      const perfilData = perfilSnap.data();
      const clienteId = perfilData.clienteId;
      const clienteEmail = perfilData.clienteEmail;
      
      if (!clienteId && !clienteEmail) {
        return {
          tieneCredenciales: false,
          mensaje: 'Este perfil no tiene credenciales asignadas'
        };
      }
      
      // Buscar usuario en Firestore
      let usuarioData = null;
      if (clienteId) {
        const usuarioRef = doc(db, 'usuarios', clienteId);
        const usuarioSnap = await getDoc(usuarioRef);
        if (usuarioSnap.exists()) {
          usuarioData = { id: usuarioSnap.id, ...usuarioSnap.data() };
        }
      } else if (clienteEmail) {
        const usuariosQuery = query(
          collection(db, 'usuarios'),
          where('email', '==', clienteEmail),
          limit(1)
        );
        const usuariosSnapshot = await getDocs(usuariosQuery);
        if (!usuariosSnapshot.empty) {
          usuarioData = { id: usuariosSnapshot.docs[0].id, ...usuariosSnapshot.docs[0].data() };
        }
      }
      
      // Verificar si existe en Firebase Auth
      let existeEnAuth = false;
      if (clienteEmail) {
        try {
          const signInMethods = await fetchSignInMethodsForEmail(auth, clienteEmail);
          existeEnAuth = signInMethods.length > 0;
        } catch (error) {
          console.warn('Error verificando Auth:', error);
        }
      }
      
      return {
        tieneCredenciales: existeEnAuth && usuarioData !== null,
        clienteId: clienteId || usuarioData?.id,
        clienteEmail: clienteEmail || usuarioData?.email,
        clienteNombre: perfilData.clienteNombre || usuarioData?.nombre,
        credencialesAsignadas: perfilData.credencialesAsignadas || false,
        fechaCredencialesAsignadas: perfilData.fechaCredencialesAsignadas,
        usuarioData: usuarioData
      };
    } catch (error) {
      console.error('Error obteniendo info de credenciales:', error);
      throw error;
    }
  }

  /**
   * Enviar email de restablecimiento de contraseña al cliente
   * @param {string} email - Email del cliente
   * @returns {Promise<void>}
   */
  async enviarRestablecimientoPassword(email) {
    try {
      await sendPasswordResetEmail(auth, email, {
        url: `${getAuthContinueUrlBase()}/login`,
        handleCodeInApp: false
      });
      
      // Registrar envío
      await this.registrarIntentoEmail(email, {
        tipo: 'restablecimiento_password',
        accion: 'enviado_por_concesionaria'
      }, true);
      
      return { success: true };
    } catch (error) {
      console.error('Error enviando restablecimiento de contraseña:', error);
      throw error;
    }
  }

  /**
   * Reenviar credenciales por email
   * @param {Object} datosCliente - Datos del cliente
   * @param {Object} datosPerfil - Datos del perfil
   * @param {string} nuevaPassword - Nueva contraseña (opcional, se genera si no se proporciona)
   * @returns {Promise<Object>}
   */
  async reenviarCredenciales(datosCliente, datosPerfil, nuevaPassword = null) {
    try {
      const password = nuevaPassword || this.generarPasswordTemporal();
      
      // Enviar email con credenciales
      await this.enviarEmailNotificacion(
        datosCliente,
        password,
        datosPerfil,
        false // No es usuario nuevo, solo reenvío
      );
      
      return {
        success: true,
        password: password,
        mensaje: 'Credenciales reenviadas exitosamente'
      };
    } catch (error) {
      console.error('Error reenviando credenciales:', error);
      throw error;
    }
  }
}

export default new CredencialesClienteService();

